import React, { useState, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Table from '../common/Table';
import ConfirmationModal from '../common/ConfirmationModal';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useFetch, useSubmission } from '../../hooks/useCallApi';
import { businessTypeSchema } from '../../validation/businessPermit/businessRegistrationSchema';

const NewBusinessTypeModal = ({ isOpen, onClose }) => {
  const [businessTypeName, setBusinessTypeName] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(5);
  const [editingBusinessType, setEditingBusinessType] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const queryClient = useQueryClient();

  // API params for fetching business types
  const params = useMemo(() => ({
    per_page: perPage,
    page: currentPage,
  }), [perPage, currentPage]);

  // Fetch business types
  const {
    data: businessTypesData,
    isLoading,
  } = useFetch(
    ['business-types', params],
    (api) => api.admin.businessPermitsAPI.getAllBusinessTypes(params),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      enabled: isOpen,
      onError: () => {
        console.error('Failed to fetch business types');
      },
    }
  );

  // Normalize API response
  const normalizeApiListResponse = (response) => {
    return (
      response?.data?.business_types ||
      (Array.isArray(response?.data) ? response.data : null) ||
      response?.business_types ||
      (Array.isArray(response) ? response : []) ||
      []
    );
  };

  const businessTypes = normalizeApiListResponse(businessTypesData);

  // Pagination logic - use server-side pagination data from meta object
  const totalPages = businessTypesData?.data?.meta?.last_page || 1;
  const totalItems = businessTypesData?.data?.meta?.total || 0;
  const currentPageFromServer = businessTypesData?.data?.meta?.current_page || currentPage;

  // Create business type mutation
  const { mutateAsync: createBusinessType, isLoading: isCreating } = useSubmission(
    ['create-business-type'],
    (api, data) => api.admin.businessPermitsAPI.createBusinessType(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['business-types']);
        setBusinessTypeName('');
        setShortDescription('');
      },
    }
  );

  // Update business type mutation
  const { mutateAsync: updateBusinessType, isLoading: isUpdating } = useSubmission(
    ['update-business-type'],
    (api, { id, data }) => api.admin.businessPermitsAPI.updateBusinessType(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['business-types']);
        setEditingBusinessType(null);
        setIsEditModalOpen(false);
      },
    }
  );

  // Archive business type mutation
  const { mutateAsync: archiveBusinessType } = useSubmission(
    ['archive-business-type'],
    (api, id) => api.admin.businessPermitsAPI.archiveBusinessType(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['business-types']);
      },
    }
  );

  const handleAddBusinessType = async () => {
    try {
      const formData = {
        name: businessTypeName.trim(),
        description: shortDescription.trim(),
      };
      
      await businessTypeSchema.validate(formData, { abortEarly: false });
      
      await createBusinessType(formData);
      toast.success('Business type added successfully.');
    } catch (err) {
      if (err.name === 'ValidationError') {
        err.errors.forEach(error => toast.error(error));
      } else {
        console.error('Failed to create business type:', err);
        toast.error('Failed to add business type. Please try again.');
      }
    }
  };

  const handleEdit = (item) => {
    setEditingBusinessType(item);
    setBusinessTypeName(item.name || '');
    setShortDescription(item.description || '');
    setIsEditModalOpen(true);
  };

  const handleUpdateBusinessType = async () => {
    if (!editingBusinessType) return;
    
    try {
      const formData = {
        name: businessTypeName.trim(),
        description: shortDescription.trim(),
      };
      
      await businessTypeSchema.validate(formData, { abortEarly: false });
      
      await updateBusinessType({
        id: editingBusinessType.id,
        data: formData,
      });
      setBusinessTypeName('');
      setShortDescription('');
      toast.success('Business type updated successfully.');
    } catch (err) {
      if (err.name === 'ValidationError') {
        err.errors.forEach(error => toast.error(error));
      } else {
        console.error('Failed to update business type:', err);
        toast.error('Failed to update business type. Please try again.');
      }
    }
  };

  const handleDelete = async (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await archiveBusinessType(itemToDelete.id);
      toast.success(`"${itemToDelete.name}" has been archived.`);
      setDeleteModalOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Failed to archive business type:', err);
      toast.error('Failed to archive business type. Please try again.');
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  const columns = [
    {
      key: 'name',
      label: 'TYPE',
      render: (value) => <span className="font-medium text-gray-900">{value}</span>
    },
    {
      key: 'description',
      label: 'SHORT DESCRIPTION',
      render: (value) => <span className="text-gray-600">{value}</span>
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      render: (value, row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleEdit(row)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Business Types"
      size="4xl"
    >
      <div className="space-y-6">
        {/* Add New Business Type Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Business Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name of Business Type
              </label>
              <input
                type="text"
                value={businessTypeName}
                onChange={(e) => setBusinessTypeName(e.target.value)}
                placeholder="Enter business type name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Short Description
              </label>
              <input
                type="text"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="Enter short description"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="mt-4">
            <Button
              onClick={handleAddBusinessType}
              variant="primary"
              className="px-4 py-2"
              disabled={isCreating}
            >
              {isCreating ? 'Adding...' : 'Add'}
            </Button>
          </div>
        </div>

        {/* Table Section */}
        <div>
          <Table
            columns={columns}
            data={businessTypes}
            pagination={{
              currentPage: currentPageFromServer,
              lastPage: totalPages,
              perPage: perPage,
              total: totalItems
            }}
            onPageChange={handlePageChange}
            paginationProps={{
              onPerPageChange: handlePerPageChange,
              isLoading
            }}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingBusinessType(null);
            setBusinessTypeName('');
            setShortDescription('');
          }}
          title="Edit Business Type"
          size="md"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name of Business Type
              </label>
              <input
                type="text"
                value={businessTypeName}
                onChange={(e) => setBusinessTypeName(e.target.value)}
                placeholder="Enter business type name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Short Description
              </label>
              <input
                type="text"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="Enter short description"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingBusinessType(null);
                  setBusinessTypeName('');
                  setShortDescription('');
                }}
                variant="secondary"
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateBusinessType}
                variant="primary"
                className="px-4 py-2"
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Archive Business Type"
        message={`Are you sure you want to archive "${itemToDelete?.name || 'this business type'}"? This action can be reversed.`}
        confirmText="Archive"
        cancelText="Cancel"
        isLoading={false}
      />
    </Modal>
  );
};

export default NewBusinessTypeModal;